import {onBeforeUnmount, ref, type Ref} from 'vue';

/**
 * True while some lightbox on the page waits for the library to load on its first open. Each media
 * component has its own lightbox, so a second click elsewhere in that wait would stack a second one.
 */
let openingLightbox = false;

/** Media source with URL and MIME type for the lightbox */
export interface BiggerPictureSource {
  src: string;
  type: string; // e.g. 'video/mp4', 'image/webp'
}

/** Lightbox media item supporting images, videos, iframes, and custom HTML */
export interface BiggerPictureItem {
  img?: string;
  thumb?: string;
  alt?: string;
  caption?: string;
  width?: number;
  height?: number;
  sources?: BiggerPictureSource[];
  iframe?: string;
  html?: string;
}

/** Configuration options for the bigger-picture lightbox instance */
export interface BiggerPictureInstanceOptions {
  scale?: number;
  intro?: string;
  maxZoom?: number;
  onOpen?(): void;
  onUpdate?(container: HTMLElement): void;
  onClose?(): void;
  onClosed?(): void;
}

/** Options for opening the lightbox with a set of items and optional starting position */
export interface BiggerPictureOpenOptions {
  items: BiggerPictureItem[];
  position?: number;
}

/** API for controlling the bigger-picture lightbox (open, close, navigate) */
export interface BiggerPictureApi {
  open(options: BiggerPictureOpenOptions): void;
  close(): void;
  next(): void;
  prev(): void;
  setPosition(position: number): void;
  /**
   * The Svelte component's own teardown: closing hides the lightbox but leaves the component alive.
   * Present at runtime, absent from the library's typings.
   */
  $destroy?(): void;
}

/** Return value of the useLightbox composable with lazy initialization and navigation methods */
export interface UseBiggerPictureReturn {
  instance: Ref<BiggerPictureApi | null>;
  initialize(): Promise<void>;
  open(options: BiggerPictureOpenOptions): Promise<void>;
  close(): void;
  next(): void;
  prev(): void;
  setPosition(position: number): void;
}

/** Provides a lazy-initialized lightbox (bigger-picture) for viewing images, videos, and iframes with lifecycle cleanup */
export function useLightbox(
  options?: BiggerPictureInstanceOptions,
): UseBiggerPictureReturn {
  const instance: Ref<BiggerPictureApi | null> = ref(null);
  let initPromise: Promise<void> | null = null;
  let holdsOpeningLock = false;
  let unmounted = false;

  /**
   * Destroys the lightbox, open or not: destroying an open one tears its overlay down too. Never
   * through close(), which throws on an instance that was never opened and, on one the reader has
   * already closed, leaves the library's shared `closing` flag set, which stops every later
   * lightbox from zooming.
   */
  function discard(): void {
    const current = instance.value;
    instance.value = null;
    current?.$destroy?.();
  }

  /** Gives the page-wide lock back, if this lightbox holds it. */
  function releaseOpeningLock(): void {
    if (holdsOpeningLock) {
      holdsOpeningLock = false;
      openingLightbox = false;
    }
  }

  async function initialize(): Promise<void> {
    if (instance.value) {
      return;
    }

    if (initPromise) {
      return initPromise;
    }

    // SSR guard
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    initPromise = (async(): Promise<void> => {
      try {
        const module = await import('bigger-picture');

        const biggerPicture = module.default as (
          config: BiggerPictureInstanceOptions & {target: HTMLElement},
        ) => BiggerPictureApi;

        instance.value = biggerPicture({
          target: document.body,
          scale: 0.95,
          ...options,
        });
        if (unmounted) {
          // The page went while the library loaded: nothing is left to close it later.
          discard();
        }
      } finally {
        initPromise = null;
      }
    })();

    return initPromise;
  }

  async function open(openOptions: BiggerPictureOpenOptions): Promise<void> {
    if (openingLightbox) {
      return;
    }
    openingLightbox = true;
    holdsOpeningLock = true;
    try {
      await initialize();
      // Null when the page went while the library loaded: initialize has already destroyed it.
      instance.value?.open(openOptions);
    } finally {
      releaseOpeningLock();
    }
  }

  function close(): void {
    instance.value?.close();
  }

  function next(): void {
    instance.value?.next();
  }

  function prev(): void {
    instance.value?.prev();
  }

  function setPosition(position: number): void {
    instance.value?.setPosition(position);
  }

  onBeforeUnmount(() => {
    unmounted = true;
    // A load that never settles must not leave every other lightbox on the site unopenable.
    releaseOpeningLock();
    discard();
  });

  return {
    instance,
    initialize,
    open,
    close,
    next,
    prev,
    setPosition,
  };
}
